import React, { useEffect, useState } from "react";
import { Input, Modal, Spin } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ApiDimension,
  createDimension,
  deleteDimension,
  replaceDimensionCategories,
  updateDimension,
} from "./balancedScorecard.service";
import "./ManageDimensionsModal.css";

// Draft state lives only in this modal until "Save changes" is pressed.
interface DraftCategory {
  key: string;
  id?: string;
  name: string;
}

interface DraftDimension {
  key: string;
  id?: string;
  name: string;
  categories: DraftCategory[];
  original?: { name: string; categories: { id: string; name: string }[] };
}

interface ManageDimensionsModalProps {
  open: boolean;
  scorecardId: string;
  dimensions: ApiDimension[];
  canManage: boolean;
  onClose: () => void;
  /** Called after any successful write so the parent reloads dimensions. */
  onChanged: () => void;
}

let keySeq = 0;
const nextKey = (prefix: string) => `${prefix}-${++keySeq}`;

const toDraft = (d: ApiDimension): DraftDimension => ({
  key: d.id,
  id: d.id,
  name: d.name,
  categories: d.categories.map((c) => ({ key: c.id, id: c.id, name: c.name })),
  original: {
    name: d.name,
    categories: d.categories.map((c) => ({ id: c.id, name: c.name })),
  },
});

const blankDraft = (): DraftDimension => ({
  key: nextKey("new-dim"),
  name: "",
  categories: [],
});

// Blank category rows are ignored (a row only counts once it has a name).
const cleanCategories = (d: DraftDimension) =>
  d.categories
    .filter((c) => c.name.trim())
    .map((c) => ({ ...(c.id ? { id: c.id } : {}), name: c.name.trim() }));

const countCategories = (d: DraftDimension) =>
  new Set(
    d.categories.map((c) => c.name.trim().toLowerCase()).filter(Boolean),
  ).size;

const isBlankNewDraft = (d: DraftDimension) =>
  !d.id && !d.name.trim() && cleanCategories(d).length === 0;

const categoriesChanged = (d: DraftDimension) => {
  const orig = d.original?.categories ?? [];
  const cur = cleanCategories(d);
  return (
    cur.length !== orig.length ||
    cur.some((c, i) => c.id !== orig[i].id || c.name !== orig[i].name)
  );
};

const isDraftDirty = (d: DraftDimension) => {
  if (!d.id) return !isBlankNewDraft(d);
  return d.name.trim() !== d.original?.name || categoriesChanged(d);
};

const validate = (drafts: DraftDimension[], t: TFunction): string | null => {
  const dimNames = new Set<string>();
  for (const d of drafts) {
    if (isBlankNewDraft(d)) continue;
    if (!d.name.trim()) return t("dimensions.nameRequired");
    const dimKey = d.name.trim().toLowerCase();
    if (dimNames.has(dimKey)) {
      return t("dimensions.duplicateName", { name: d.name.trim() });
    }
    dimNames.add(dimKey);

    const catNames = new Set<string>();
    for (const c of cleanCategories(d)) {
      const catKey = c.name.toLowerCase();
      if (catNames.has(catKey)) {
        return t("dimensions.duplicateCategory", {
          name: c.name,
          dimension: d.name.trim(),
        });
      }
      catNames.add(catKey);
    }
  }
  return null;
};

const errorCode = (err: any): string | undefined =>
  err?.response?.data?.detail?.code;

const messageFor = (err: any, t: TFunction): string => {
  const code = errorCode(err);
  if (code === "bsc_dimension_name_exists") {
    return t("dimensions.nameExists");
  }
  if (code === "bsc_category_not_in_dimension") {
    return t("dimensions.categoriesChanged");
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return t("dimensions.saveFailed");
};

const askConfirm = (
  title: string,
  content: React.ReactNode,
  okText: string,
) =>
  new Promise<boolean>((resolve) => {
    Modal.confirm({
      title,
      content,
      okText,
      okButtonProps: { danger: true },
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

const ManageDimensionsModal: React.FC<ManageDimensionsModalProps> = ({
  open,
  scorecardId,
  dimensions,
  canManage,
  onClose,
  onChanged,
}) => {
  const { t } = useTranslation("balancedScorecard");
  const [drafts, setDrafts] = useState<DraftDimension[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Delete flow: first confirm, then a second step if indicators depend on it.
  const [deleteTarget, setDeleteTarget] = useState<{
    draft: DraftDimension;
    indicatorCount: number | null;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Reset the drafts from the server copy every time the modal opens.
  useEffect(() => {
    if (!open) return;
    const initial = dimensions.map(toDraft);
    setDrafts(initial);
    setActiveKey(initial[0]?.key ?? null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const active = drafts.find((d) => d.key === activeKey) ?? null;
  const dirty = drafts.some(isDraftDirty);

  const updateDraft = (
    key: string,
    fn: (d: DraftDimension) => DraftDimension,
  ) => setDrafts((ds) => ds.map((d) => (d.key === key ? fn(d) : d)));

  const addDimension = () => {
    const draft = blankDraft();
    setDrafts((ds) => [...ds, draft]);
    setActiveKey(draft.key);
  };

  const addCategory = () => {
    if (!active) return;
    const cat: DraftCategory = { key: nextKey("new-cat"), name: "" };
    updateDraft(active.key, (d) => ({
      ...d,
      categories: [...d.categories, cat],
    }));
  };

  const removeCategory = (catKey: string) => {
    if (!active) return;
    updateDraft(active.key, (d) => ({
      ...d,
      categories: d.categories.filter((c) => c.key !== catKey),
    }));
  };

  const renameCategory = (catKey: string, name: string) => {
    if (!active) return;
    updateDraft(active.key, (d) => ({
      ...d,
      categories: d.categories.map((c) =>
        c.key === catKey ? { ...c, name } : c,
      ),
    }));
  };

  // Replaces the category list. If the server says categories still hold
  // values, ask once and retry with force. Returns false if the user cancels.
  const putCategories = async (
    dimensionId: string,
    categories: { id?: string; name: string }[],
  ): Promise<boolean> => {
    try {
      await replaceDimensionCategories(scorecardId, dimensionId, categories);
      return true;
    } catch (err: any) {
      if (errorCode(err) !== "bsc_categories_have_values") throw err;
      const detail = err.response.data.detail;
      const ok = await askConfirm(
        t("dimensions.removeValuesTitle"),
        t("dimensions.removeValuesBody", {
          categories: detail.categories.join(", "),
          count: detail.value_count,
        }),
        t("dimensions.continue"),
      );
      if (!ok) return false;
      await replaceDimensionCategories(scorecardId, dimensionId, categories, true);
      return true;
    }
  };

  const handleSave = async () => {
    const problem = validate(drafts, t);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      for (const d of drafts) {
        if (!d.id) {
          if (isBlankNewDraft(d)) continue;
          await createDimension(scorecardId, {
            name: d.name.trim(),
            categories: cleanCategories(d).map((c) => c.name),
          });
          continue;
        }
        if (d.name.trim() !== d.original?.name) {
          await updateDimension(scorecardId, d.id, { name: d.name.trim() });
        }
        if (categoriesChanged(d)) {
          const done = await putCategories(d.id, cleanCategories(d));
          if (!done) return; // user kept the values; stop without closing
        }
      }
      toast.success(t("dimensions.saved"));
      onChanged();
      onClose();
    } catch (err: any) {
      // Earlier dimensions in the loop may already be saved, so reload.
      setError(messageFor(err, t));
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  const removeDraft = (key: string) => {
    setDrafts((ds) => {
      const next = ds.filter((d) => d.key !== key);
      if (activeKey === key) setActiveKey(next[0]?.key ?? null);
      return next;
    });
  };

  const requestDelete = (draft: DraftDimension) => {
    if (!draft.id) {
      removeDraft(draft.key);
      return;
    }
    setDeleteTarget({ draft, indicatorCount: null });
  };

  const confirmDelete = async () => {
    const dimensionId = deleteTarget?.draft.id;
    if (!deleteTarget || !dimensionId) return;
    const { draft, indicatorCount } = deleteTarget;
    setDeleting(true);
    try {
      await deleteDimension(scorecardId, dimensionId, indicatorCount != null);
      setDeleteTarget(null);
      removeDraft(draft.key);
      toast.success(t("dimensions.deleted"));
      onChanged();
    } catch (err: any) {
      if (errorCode(err) === "bsc_dimension_in_use") {
        // Second step: the dialog stays open and now names the impact.
        setDeleteTarget({
          draft,
          indicatorCount: err.response.data.detail.indicator_count ?? 0,
        });
      } else {
        setDeleteTarget(null);
        toast.error(messageFor(err, t));
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        title={t("dimensions.title")}
        onCancel={onClose}
        footer={null}
        width={720}
        destroyOnClose
        className="bsc-dim-modal"
      >
        <p className="bsc-dim-subtitle">{t("dimensions.subtitle")}</p>

        <div className="bsc-dim-body">
          <div className="bsc-dim-list">
            {drafts.map((d) => (
              <button
                key={d.key}
                type="button"
                className={`bsc-dim-list-item ${d.key === activeKey ? "is-active" : ""}`}
                onClick={() => setActiveKey(d.key)}
              >
                <span className="bsc-dim-list-name">
                  {d.name.trim() || t("dimensions.untitled")}
                </span>
                <span className="bsc-dim-count">{countCategories(d)}</span>
              </button>
            ))}
            <button
              type="button"
              className="bsc-dim-new"
              onClick={addDimension}
              disabled={!canManage}
            >
              <PlusOutlined /> {t("dimensions.newDimension")}
            </button>
          </div>

          <div className="bsc-dim-form">
            {active ? (
              <>
                <label className="bsc-dim-label">
                  {t("dimensions.name")}
                </label>
                <div className="bsc-dim-name-row">
                  <Input
                    value={active.name}
                    placeholder={t("dimensions.namePlaceholder")}
                    disabled={!canManage}
                    onChange={(e) =>
                      updateDraft(active.key, (d) => ({
                        ...d,
                        name: e.target.value,
                      }))
                    }
                  />
                  <button
                    type="button"
                    className="bsc-dim-icon-btn"
                    aria-label={t("dimensions.deleteAria")}
                    disabled={!canManage}
                    onClick={() => requestDelete(active)}
                  >
                    <DeleteOutlined />
                  </button>
                </div>

                <label className="bsc-dim-label">
                  {t("dimensions.categories")}
                </label>
                <div className="bsc-dim-categories">
                  {active.categories.length === 0 && (
                    <div className="bsc-dim-cat-empty">
                      {t("dimensions.noCategories")}
                    </div>
                  )}
                  {active.categories.map((c) => (
                    <div className="bsc-dim-cat-row" key={c.key}>
                      <Input
                        value={c.name}
                        placeholder={t("dimensions.categoryPlaceholder")}
                        disabled={!canManage}
                        onChange={(e) => renameCategory(c.key, e.target.value)}
                      />
                      <button
                        type="button"
                        className="bsc-dim-icon-btn"
                        aria-label={t("dimensions.removeCategoryAria")}
                        disabled={!canManage}
                        onClick={() => removeCategory(c.key)}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="bsc-dim-add-cat"
                  onClick={addCategory}
                  disabled={!canManage}
                >
                  <PlusOutlined /> {t("dimensions.newCategory")}
                </button>
              </>
            ) : (
              <div className="bsc-dim-empty">{t("dimensions.empty")}</div>
            )}
          </div>
        </div>

        {error && <div className="bsc-dim-error">{error}</div>}

        <div className="bsc-dim-footer">
          <button type="button" className="bsc-btn-cancel" onClick={onClose}>
            {t("dimensions.cancel")}
          </button>
          <button
            type="button"
            className="bsc-dim-save"
            onClick={handleSave}
            disabled={!canManage || !dirty || saving}
          >
            {saving && <Spin size="small" />} {t("dimensions.save")}
          </button>
        </div>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        title={t("dimensions.deleteTitle")}
        okText={t("dimensions.delete")}
        okButtonProps={{ danger: true }}
        onOk={confirmDelete}
        confirmLoading={deleting}
        onCancel={() => setDeleteTarget(null)}
        cancelText={t("dimensions.cancel")}
        destroyOnClose
      >
        {deleteTarget?.indicatorCount == null ? (
          <p>{t("dimensions.deleteBody")}</p>
        ) : (
          <p>
            {t("dimensions.deleteImpactBody", {
              count: deleteTarget.indicatorCount,
            })}
          </p>
        )}
      </Modal>
    </>
  );
};

export default ManageDimensionsModal;
